import React, { useCallback, useEffect, useMemo, useState } from "react";
import RaisedCard from "../../../components/RaisedCard";
import { Button, Flex, Image, Link, Text } from "@aws-amplify/ui-react";
import useDebounce from "../../../hooks/useDebounce";
import { FaTimes } from "react-icons/fa";
import { useUserContext } from "../../../context";
import goldCrown from "../../../assets/images/icons/crown.png";
import useToggle from "../../../hooks/useToggle";
import { useNavigate } from "react-router-dom";
import { useFreeTrials } from "../hooks/useFreeTrials";

type FreeTrialToastProps = {};

export function FreeTrialToast({}: FreeTrialToastProps) {
  const navigate = useNavigate();

  const [timeLeft, setTimeLeft] = useState<string | undefined>(undefined);
  const [toggle, setToggle] = useToggle(true);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const { user, isSubscribed } = useUserContext();

  useEffect(() => {
    updateTimeLeft();
  }, []);

  const { data: freeTrials } = useFreeTrials();

  const currentTrial = useMemo(() => {
    return freeTrials?.find((trial) => {
      return trial.trialStatus === "active";
    });
  }, [freeTrials]);

  const recentlyExpiredTrial = useMemo(() => {
    return freeTrials?.find((trial) => {
      return (
        trial.trialStatus !== "active" &&
        Date.now() - Date.parse(trial.expiry) < 1000 * 60 * 60 * 24 * 3
      );
    });
  }, [freeTrials]);

  // const { mutateAsync: endTrial_mutateAsync } = useMutation({
  //   mutationKey: ["end-trial"],
  //   mutationFn: async () => {
  //     if (!user) return;
  //     await updateUser(user.creatorId, {
  //       customerTier: "Starter",
  //     });
  //     await RestAPI.put("/free-trials", {
  //       body: {
  //         ...currentTrial,
  //         status: "expired",
  //       },
  //     });
  //   },
  //   onSuccess: () => {
  //     queryClient.invalidateQueries(["user"]);
  //     queryClient.invalidateQueries(["free-trial"]);
  //     setTimeLeft(undefined);
  //   },
  // });

  function calculateTimeLeft(expiry: string | null | undefined): {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } {
    if (expiry === undefined || expiry === null)
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    const now = Date.now();
    const timeLeft = Date.parse(expiry) - now;
    const daysLeft = Math.max(0, Math.round(timeLeft / (1000 * 60 * 60 * 24)));
    const hoursLeft = Math.max(
      0,
      Math.floor((timeLeft / (1000 * 60 * 60)) % 24)
    );
    const minutesLeft = Math.max(0, Math.floor((timeLeft / (1000 * 60)) % 60));
    const secondsLeft = Math.max(0, Math.floor(timeLeft / 1000));

    return {
      days: daysLeft,
      hours: hoursLeft,
      minutes: minutesLeft,
      seconds: secondsLeft,
    };
  }

  const updateTimeLeft = useCallback(() => {
    setTimeLeft(() => {
      const { days, hours } = calculateTimeLeft(currentTrial?.expiry);

      if (days > 0) {
        return `${days} days`;
      } else if (hours > 0) {
        return `${hours} hours`;
      } else {
        return `< 1 hour`;
      }
    });
  }, [user, currentTrial]);

  useDebounce(
    () => {
      updateTimeLeft();
      setToggle();
    },
    1000,
    [user, timeLeft, toggle]
  );

  if (isSubscribed) return null;

  if (currentTrial === undefined && recentlyExpiredTrial)
    return isOpen ? (
      <RaisedCard
        position={"fixed"}
        bottom={"small"}
        right={"large"}
        style={{ zIndex: 1000 }}
      >
        <Flex alignItems={"center"}>
          <Image alt="premium-crown" src={goldCrown} height={"20px"}></Image>

          <Text fontSize={"small"}>
            Your Free Trial has ended.{" "}
            <Link
              isExternal
              href="/dashboard/upgrade"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <b>
                <u>Upgrade Today</u>
              </b>
            </Link>
          </Text>
          <Button
            variation="link"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            <FaTimes />
          </Button>
        </Flex>
      </RaisedCard>
    ) : null;

  if (!currentTrial) return null;

  return isOpen ? (
    <RaisedCard
      position={"fixed"}
      bottom={"small"}
      right={"large"}
      style={{ zIndex: 1000 }}
    >
      <Flex alignItems={"center"}>
        <Image alt="premium-crown" src={goldCrown} height={"20px"}></Image>

        <Text fontSize={"small"}>
          Your Free Trial of{" "}
          <Link
            onClick={() => {
              navigate("/dashboard/upgrade");
            }}
          >
            <u>Gibbly Premium</u>
          </Link>{" "}
          ends in <b>{timeLeft}</b>
        </Text>
        <Button
          variation="link"
          onClick={() => {
            setIsOpen(false);
          }}
        >
          <FaTimes />
        </Button>
      </Flex>
    </RaisedCard>
  ) : null;
}
